/*!

=========================================================
* Argon Dashboard React - v1.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/*eslint-disable*/
import React from "react";
import {Link, NavLink as NavLinkRRD} from "react-router-dom";
import Preferences from "components/Forms/Preferences";
// nodejs library to set properties for components
import {PropTypes} from "prop-types";
import {connect} from "react-redux";
import {createSelector} from "reselect";
import RegisterNewGateway from "components/RegisterNewGateway/index";

import {SearchContainer} from "../Headers/ThinHeader";
import ProductLogo from "components/ProductLogo";
import {withTranslation} from "react-i18next";
import mapSearchItemsToProps, {
  isSearchableInScope,
  searchItemMatches,
} from "components/Headers/mapSearchItemsToProps";

import "./sidebar.css";

// reactstrap components
import {
  Col,
  Collapse,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  InputGroup,
  InputGroupText,
  Nav,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
  Row,
  Tooltip,
  UncontrolledDropdown,
} from "reactstrap";

import keys from "configs/constants";
import utils from "utils";
import api from "services/backendService";
import {AuthorizationContext} from "contexts/AuthorizationContext";
import OrganizationDropdown from "../OrganizationDropdown";
import OrgFilterChip from "../OrgFilter/OrgFilterChip";
import SiteFilterChip from "../OrgFilter/SiteFilterChip";
import CreateOrganizationModal from "../CreateOrganizationModal";
import Loader from "components/Loader";
import AuditLog from "components/AuditLog/index";
import {isFeatureEnabled} from "@redux/selectors";
import { selectSplitDashboardEnabled } from "@redux/selectors/splitDashboard";
import { selectAuditLogPageEnabled } from "@redux/selectors/auditLogPage";
import { useCanSeeApiPage } from "hooks/useApiAccess";
import SidebarInfoMenu from "components/InfoMenu/SidebarInfoMenu";

const COMPACT_STORAGE_KEY = "sidebarCompact";

const readCompactPreference = () => {
  try {
    return globalThis.localStorage?.getItem(COMPACT_STORAGE_KEY) === "1";
  } catch (_) {
    return false;
  }
};

// A body class (rather than component state only) because the main content
// margin lives outside this component.
const applyCompactClass = (isCompact) => {
  globalThis.document?.body.classList.toggle("sidebar-compact", !!isCompact);
};

class Sidebar extends React.Component {
  static contextType = AuthorizationContext;

  state = {
    collapseOpen: false,
    searchTerm: "",
    isPreferencesOpen: false,
    showNewGatewayModal: false,
    showAuditLogModal: false,
    newMacAddress: "",
    subscriptionRequiredTooltipOpen: false,
    isDocumentsDropdownOpen: false,
    organizationNameTooltipOpen: false,
    isModalCreateOrganizationOpen: false,
    isLoading: false,
    isCompact: readCompactPreference(),
  };

  componentDidMount() {
    applyCompactClass(this.state.isCompact);
  }

  componentWillUnmount() {
    applyCompactClass(false);
  }

  // Desktop only: collapse the sidebar to an icon rail (and back).
  toggleCompact = () => {
    const isCompact = !this.state.isCompact;
    this.setState({ isCompact });
    applyCompactClass(isCompact);
    try {
      globalThis.localStorage?.setItem(COMPACT_STORAGE_KEY, isCompact ? "1" : "0");
    } catch (_) {}
    // Let width-dependent widgets (e.g. Plotly charts) re-measure once the
    // width transition has finished.
    globalThis.setTimeout?.(() => globalThis.dispatchEvent?.(new Event("resize")), 220);
  };

  // Native tooltip with the label, only needed when labels are hidden.
  compactTitle = (label) => (this.state.isCompact ? label : undefined);

  subscriptionRequiredTooltipToggle = () => {
    this.setState({
      subscriptionRequiredTooltipOpen:
        !this.state.subscriptionRequiredTooltipOpen,
    });
  };

  constructor(props) {
    super(props);

    this.activeRoute.bind(this);
  }

  // verifies if routeName is the one active (in browser input)
  activeRoute(routeName) {
    return this.props.location.pathname.indexOf(routeName) > -1 ? "active" : "";
  }

  // toggles collapse between opened and closed (true/false)
  toggleCollapse = () => {
    this.setState({
      collapseOpen: !this.state.collapseOpen,
    });
  };
  // closes the collapse
  closeCollapse = () => {
    this.setState({
      collapseOpen: false,
    });
  };

  menuSeparator = () => {
    return (
      <Container fluid>
        <Row className="justify-content-center">
          <div className="menu-separator" />
        </Row>
      </Container>
    );
  };

  closeModal = () => {
    this.setState({
      newMacAddress: "",
      showNewGatewayModal: false,
    });
  };

  renderAuditLogLink = () => {
    if (!this.context.canViewAuditLog()) {
      return null;
    }

    // CS_50137_AUDIT_LOG_PAGE: the Audit log page; off, the export dialog.
    if (this.props.isAuditLogPageEnabled) {
      return (
        <NavItem className="ml-1 sidebar-item">
          <NavLinkRRD
            id="auditLogLink"
            to="/std/audlg"
            onClick={this.closeCollapse}
            className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
            title={this.compactTitle(this.props.t("sidebar.auditLog"))}
          >
            {keys.ICON_MENU_AUDIT_LOG}
            <span className="sidebar-label">{this.props.t("sidebar.auditLog")}</span>
          </NavLinkRRD>
        </NavItem>
      );
    }

    return (
      <>
        <NavItem
          className="ml-1 sidebar-item"
          onClick={() => this.setState({ showAuditLogModal: true })}
        >
          <NavLink
            id="exportAuditLog"
            title={this.compactTitle(this.props.t("sidebar.auditLog"))}
          >
            {keys.ICON_MENU_AUDIT_LOG}
            <span className="sidebar-label">{this.props.t("sidebar.auditLog")}</span>
          </NavLink>
        </NavItem>
        <AuditLog
          isOpen={this.state.showAuditLogModal}
          toggle={() =>
            this.setState({ showAuditLogModal: !this.state.showAuditLogModal })
          }
        />
      </>
    );
  };

  // creates the links that appear in the left menu / Sidebar
  createLinks = (routes, canRegisterNewGateway) => {
    const th = this;
    let showSeparator = false;
    let startMenuSidebar = this.showOrganizationDropdown();

    const activeOrgGuid = this.context.activeOrganization?.guid || null;
    // canSeeApi is injected by the SidebarWrapper (see bottom of file), which
    // reads the shared useCanSeeApiPage hook.
    const canSeeApi = this.props.canSeeApi;
    // With the organization filter the alarm count covers every organization.
    const alertsOrgGuid =
      this.context.isSuperAdmin() || this.context.isOrgFilterEnabled ? null : activeOrgGuid;
    let countAlertsForActiveOrganization = utils.countAlertsForCustomer(
      this.props.alerts,
      alertsOrgGuid,
      this.context.showHiddenSites,
      this.props.sites
    );

    return (
      <>
        {routes.map((prop, key) => {
          if (!this.props.isFleetPageEnabled && prop.name === "fleet") {
            return null;
          }
          if (!this.props.isSplitDashboardEnabled && prop.name === "sites") {
            return null;
          }
          if (!this.props.isMaintenancePageEnabled && prop.name === "maintenanceWindows") {
            return null;
          }
          if (prop.name === "api" && !canSeeApi) {
            return null;
          }
          if (prop.menu === false) return "";
          if (th.props.onlyAdmin) {
            if (!prop.layout.includes("/ad")) {
              return;
            } else if (!utils.isUserAdmin() && !prop.superUser) {
              return;
            }
          } else {
            if (prop.layout.includes("/ad")) {
              return;
            }
          }
          showSeparator = startMenuSidebar;
          startMenuSidebar = false;

          let linkElement;
          if (prop.link) {
            linkElement = (
              <NavItem key={key} className="ml-1">
                {showSeparator ? th.menuSeparator() : null}
                <NavLink
                  to={prop.link}
                  target="_blank"
                  tag={NavLinkRRD}
                  title={th.compactTitle(prop.name)}
                >
                  {prop.icon}
                  <span className="sidebar-label">{prop.name}</span>
                </NavLink>
              </NavItem>
            );
          } else {
            const textColor =
              prop.name === "alarmLog" && countAlertsForActiveOrganization > 0
                ? "text-danger"
                : "";
            linkElement = (
              <NavItem key={key} className="ml-1">
                {showSeparator ? th.menuSeparator() : null}
                <NavLinkRRD
                  to={prop.layout + prop.path}
                  onClick={this.closeCollapse}
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                  title={th.compactTitle(th.props.t("sidebar." + prop.name))}
                >
                  <i
                    {...prop.icon.props}
                    className={`${prop.icon.props.className} ${textColor}`}
                  />
                  <span className="sidebar-label">
                    {th.props.t("sidebar." + prop.name)}
                  </span>
                </NavLinkRRD>
              </NavItem>
            );
          }

          return linkElement;
        })}
        {th.props.onlyAdmin ? null : (
          <>
            {th.menuSeparator()}
            {this.renderAuditLogLink()}
            <NavItem
              className="ml-1 sidebar-item"
              onClick={() =>
                canRegisterNewGateway &&
                this.setState({
                  showNewGatewayModal: !this.state.showNewGatewayModal,
                })
              }
            >
              <NavLink
                id="registerNewGateway"
                disabled={!canRegisterNewGateway}
                title={th.compactTitle(th.props.t("sidebar.registerNewGateway"))}
              >
                {keys.ICON_MENU_ADD}
                <span className="sidebar-label">
                  {th.props.t("sidebar.registerNewGateway")}
                </span>
                {!canRegisterNewGateway && (
                  <div className="item-right-icon">
                    <span>{keys.ICON_WARNING}</span>
                  </div>
                )}
              </NavLink>
              {!canRegisterNewGateway && (
                <Tooltip
                  placement="right"
                  isOpen={this.state.subscriptionRequiredTooltipOpen}
                  target="registerNewGateway"
                  toggle={this.subscriptionRequiredTooltipToggle}
                >
                  {th.props.t("sidebar.subscriptionRequired")}
                </Tooltip>
              )}
            </NavItem>
            <RegisterNewGateway
              isOpen={this.state.showNewGatewayModal}
              toggle={this.closeModal}
              value={this.state.newMacAddress}
              onChange={(e) => {
                this.setState({ newMacAddress: e.target.value });
              }}
            />
          </>
        )}
      </>
    );
  };

  handleClick = (e) => {
    if (e.currentTarget.getAttribute("value") === "logout") {
      this.props.onSignOut();
    }
  };

  returnDocLinks() {
    if (keys.SHOW_CREATIVETIM_DOC === "yes") {
      return (
        <>
          {/* Divider */}
          <hr className="my-3" />
          {/* Heading */}
          <h6 className="navbar-heading text-muted">Documentation</h6>
          {/* Navigation */}
          <Nav className="mb-md-3" navbar>
            <NavItem>
              <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/overview?ref=adr-admin-sidebar">
                <i className="ni ni-spaceship" />
                Getting started
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/colors?ref=adr-admin-sidebar">
                <i className="ni ni-palette" />
                Foundation
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink href="https://demos.creative-tim.com/argon-dashboard-react/#/documentation/alerts?ref=adr-admin-sidebar">
                <i className="ni ni-ui-04" />
                Components
              </NavLink>
            </NavItem>
          </Nav>
          <Nav className="mb-md-3" navbar>
            <NavItem className="active-pro active">
              <NavLink href="https://www.creative-tim.com/product/argon-dashboard-pro-react?ref=adr-admin-sidebar">
                <i className="ni ni-spaceship" />
                Upgrade to PRO
              </NavLink>
            </NavItem>
          </Nav>
        </>
      );
    } else return "";
  }

  onInputChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  dynamicSearch = () => {
    const term = this.state.searchTerm.trim().toLowerCase();
    if (term === "") return [];
    return this.props.searchItems.filter(
      (item) => isSearchableInScope(item, this.context) && searchItemMatches(item, term)
    );
  };

  clearSearch = () => {
    this.setState({ searchTerm: "" });
    this.toggleCollapse();
  };

  togglePreferencesModal = (status = undefined) => {
    this.setState({
      isPreferencesOpen:
        typeof status !== "boolean" ? !this.state.isPreferencesOpen : status,
    });
  };

  // The organization named under the user in the mobile header: with the
  // organization filter, the filtered one, super admins included (as the
  // desktop header); without it, the active one when there is a choice.
  mobileHeaderOrganization = () => {
    if (this.context.isOrgFilterEnabled) return this.context.filteredOrganization ?? null;
    return this.showOrganizationDropdown() ? this.context.activeOrganization : null;
  };

  showOrganizationDropdown = () => {
    if (this.context.isOrgFilterEnabled) return false;
    let minOrganizations = 1;
    if (
      this.context.isOrgManagementEnabled(this.context.activeOrganization?.guid)
    )
      minOrganizations = 0;

    return (
      this.context.activeOrganization &&
      this.context.organizations.length > minOrganizations &&
      !this.context.isSuperAdmin()
    );
  };

  updateActiveOrganization = async (orgGuid) => {
    await this.context.setActiveOrganization(orgGuid);
    const currentPath = window.location.pathname || "";
    if (currentPath !== "/std/index") {
      window.location.replace("/std/index");
    }
  };

  hasOrganizations = () => {
    return this.context.groups.length > 0;
  };

  createOrganization = async (nameNewOrganization) => {
    this.setState({ isLoading: true, isModalCreateOrganizationOpen: false });
    try {
      const response = await api.addCustomer(nameNewOrganization);
      if (response.status === 201) {
        await this.updateActiveOrganization(response.data);
        await this.context.initialize();
      }
    } catch (_) {}
    this.setState({ isLoading: false });
  };

  render() {
    const username = this.props.auth.user.idToken.name;
    const { routes, logo } = this.props;
    const canRegisterNewGateway = this.context.canRegisterNewGateway();

    const creativeTimDocSection = this.returnDocLinks();
    let navbarBrandProps;
    if (logo && logo.innerLink) {
      navbarBrandProps = {
        to: logo.innerLink,
        tag: Link,
      };
    } else if (logo && logo.outterLink) {
      navbarBrandProps = {
        href: logo.outterLink,
        target: "_blank",
      };
    }

    const itemsFound = this.dynamicSearch();
    const shouldHideSearch = !this.context.isPaidSubscription();
    const headerOrganization = this.mobileHeaderOrganization();
    const searchResults = shouldHideSearch ? [] : itemsFound;

    return (
      <>
        {this.state.isLoading && <Loader />}
        <Preferences
          isOpen={this.state.isPreferencesOpen}
          toggle={this.togglePreferencesModal}
          auth={this.props.auth}
          setUser={this.props.setUser}
          t={this.props.t}
        />
        {/* Rendered BEFORE the Navbar: the layout relies on the
            ".navbar-vertical + .main-content" sibling selector for the content
            margin, so nothing may sit between the Navbar and .main-content. */}
        <button
          type="button"
          className="sidebar-compact-toggle"
          onClick={this.toggleCompact}
          aria-controls="sidenav-main"
          aria-expanded={!this.state.isCompact}
          aria-label={this.props.t(
            this.state.isCompact ? "sidebar.expandSidebar" : "sidebar.collapseSidebar"
          )}
          title={this.props.t(
            this.state.isCompact ? "sidebar.expandSidebar" : "sidebar.collapseSidebar"
          )}
        >
          <i
            className={`fas fa-chevron-${this.state.isCompact ? "right" : "left"}`}
            aria-hidden="true"
          />
        </button>
        <Navbar
          className="navbar-vertical fixed-left navbar-light bg-white"
          expand="md"
          id="sidenav-main"
        >
          <div className="sidebar-container">
            {/* Toggler */}
            <button
              className="navbar-toggler sidebar-menu m-0"
              type="button"
              onClick={this.toggleCollapse}
            >
              {keys.ICON_SIDEBAR_MENU}
            </button>
            {/* Brand */}
            {logo ? (
              <NavbarBrand {...navbarBrandProps}>
                <div className="d-none d-md-block sidebar-brand-full">
                  <ProductLogo logoWidth="w-75" />
                </div>
                {/* Compact rail: just the "C" of the Copeland wordmark, cropped
                    from the real logo so it stays on-brand. */}
                <div className="d-none d-md-block sidebar-brand-mark" aria-hidden="true">
                  <img src={keys.LOGO_URL} alt="" />
                </div>
                <div className="d-md-none">
                  <ProductLogo logoWidth="w-50" />
                </div>
              </NavbarBrand>
            ) : null}
            {/* User */}
            <Nav className="align-items-center d-md-none sidebar-user">
              <UncontrolledDropdown nav>
                <DropdownToggle nav>
                  <div className="ml-2 d-lg-block sidebar-profile-text">
                    <span className="mb-0 small font-weight-bold">
                      {username}
                    </span>
                    <br />
                    <div className="text-center">
                      {utils.isUserAdmin() && (
                        <span className="badge bg-danger text-white">
                          SuperAdmin
                        </span>
                      )}
                    </div>
                  </div>
                </DropdownToggle>
                {headerOrganization && (
                  <>
                    <div
                      className={`sidebar-profile-text${this.context.isOrgFilterEnabled ? " is-filtered" : ""}`}
                      id="orgnization-name"
                      data-testid="mobile-header-organization"
                      title={headerOrganization.name}
                    >
                      <span className="mr-1">{keys.ICON_CUSTOMER}</span>
                      <span className="mb-0 small font-weight-bold">
                        {headerOrganization.name}
                      </span>
                    </div>
                    <Tooltip
                      placement="left"
                      target="orgnization-name"
                      isOpen={this.state.organizationNameTooltipOpen}
                      toggle={() =>
                        this.setState({
                          organizationNameTooltipOpen:
                            !this.state.organizationNameTooltipOpen,
                        })
                      }
                    >
                      {headerOrganization.name}
                    </Tooltip>
                  </>
                )}
                <DropdownMenu className="dropdown-menu-arrow" end>
                  <DropdownItem className="noti-title" header tag="div">
                    <h6 className="text-overflow m-0">
                      {this.props.t("navbar.welcome") + "!"}
                    </h6>
                  </DropdownItem>
                  <DropdownItem to="/admin/user-profile" tag={Link}>
                    <i className="ni ni-single-02" />
                    <span>{this.props.t("navbar.myProfile")}</span>
                  </DropdownItem>
                  {/* Preferences */}
                  <div
                    className="dropdown-item"
                    value="preferences"
                    onClick={this.togglePreferencesModal}
                  >
                    <span className="btn-inner--icon">
                      {keys.ICON_PREFERENCES}
                    </span>
                    <span className="ml-3 btn-inner--text cursor-default">
                      {this.props.t("navbar.preferences")}
                    </span>
                  </div>
                  <DropdownItem divider />
                  <DropdownItem
                    href="#pablo"
                    onClick={(e) => e.preventDefault()}
                  >
                    <div value="logout" onClick={this.handleClick.bind(this)}>
                      <i className="ni ni-user-run" />
                      <span className="ml-3 btn-inner--text">
                        {this.props.t("navbar.logout")}
                      </span>
                    </div>
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
            {/* Collapse */}
            <Collapse navbar isOpen={this.state.collapseOpen}>
              {/* Collapse header */}
              <div className="navbar-collapse-header d-md-none ">
                <Row>
                  {logo ? (
                    <Col>
                      <NavbarBrand {...navbarBrandProps}>
                        <div className="d-md-none">
                          <ProductLogo logoWidth="w-75" noText />
                        </div>
                      </NavbarBrand>
                    </Col>
                  ) : null}
                  <Col className="collapse-close" xs="6">
                    <button
                      className="navbar-toggler"
                      type="button"
                      onClick={this.toggleCollapse}
                    >
                      <span />
                      <span />
                    </button>
                  </Col>
                </Row>
              </div>
              {/* Form */}
              {!shouldHideSearch && (
                <>
                  <Form className="mt-4 mb-3 d-md-none">
                    <InputGroup className="input-group-rounded input-group-merge">
                      <Input
                        aria-label="Search"
                        className="form-control-rounded form-control-prepended"
                        placeholder={this.props.t("sidebar.search")}
                        type="search"
                        value={this.state.searchTerm}
                        onChange={this.onInputChange}
                      />
                      <InputGroupText>
                        <span className="fa fa-search" />
                      </InputGroupText>
                    </InputGroup>
                  </Form>
                  <SearchContainer
                    items={searchResults}
                    searchTerm={this.state.searchTerm}
                    onSelect={this.clearSearch}
                    t={this.props.t}
                  />
                </>
              )}
              {/* Navigation */}
              <Nav navbar className="sidebar-full-height">
                {this.context.isOrgFilterEnabled && (
                  <>
                    <OrgFilterChip isCompact={this.state.isCompact} />
                    <SiteFilterChip isCompact={this.state.isCompact} />
                  </>
                )}
                {this.showOrganizationDropdown() && (
                  <OrganizationDropdown
                    iconColor={
                      utils.countAlertsForCustomer(
                        this.props.alerts,
                        null,
                        this.context.showHiddenSites,
                        this.props.sites
                      ) > 0
                        ? "text-warning"
                        : "text-primary"
                    }
                    title={this.context.activeOrganization.name}
                    listItems={this.context.organizations.map((org) => ({
                      id: org.id,
                      text: org.name,
                      isAlert:
                        utils.countAlertsForCustomer(
                          this.props.alerts,
                          org.guid,
                          this.context.showHiddenSites,
                          this.props.sites
                        ) > 0,
                      command: () => this.updateActiveOrganization(org.guid),
                    }))}
                    onCreateOrganization={() =>
                      this.setState({ isModalCreateOrganizationOpen: true })
                    }
                    organizations={this.context.organizations}
                    isOrgManagementEnabled={this.context.isOrgManagementEnabled}
                    t={this.props.t}
                  />
                )}
                {this.hasOrganizations() &&
                  this.createLinks(routes, canRegisterNewGateway)}
                <span className="sidebar-docs-separator">
                  {this.menuSeparator()}
                </span>
                <div className="mt-md-auto">
                  <SidebarInfoMenu
                    t={this.props.t}
                    isSuperAdmin={this.context.isSuperAdmin()}
                    isCompact={this.state.isCompact}
                  />
                </div>
              </Nav>

              {creativeTimDocSection}
            </Collapse>
          </div>
        </Navbar>
        <CreateOrganizationModal
          toggle={() => this.setState({ isModalCreateOrganizationOpen: false })}
          isOpen={this.state.isModalCreateOrganizationOpen}
          onConfirm={(e) => this.createOrganization(e)}
          t={this.props.t}
        />
      </>
    );
  }
}

Sidebar.defaultProps = {
  routes: [{}],
};

Sidebar.propTypes = {
  // links that will be displayed inside the component
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the image src of the logo
    imgSrc: PropTypes.string.isRequired,
    // the alt for the img
    imgAlt: PropTypes.string.isRequired,
  }),
};

const getAlerts = createSelector(
  (state) => state.alerts,
  (alerts) => {
    return alerts;
  }
);

const getGateways = createSelector(
  (state) => state.gateways,
  (gateways) => {
    let gws = [];

    Object.keys(gateways.byId).forEach((gwGuid) => {
      const gw = gateways.byId[gwGuid];

      gws.push({
        name: gw.name,
        macAddress: gw.macAddress,
        licenses: gw.licenses,
      });
    });

    return gws;
  }
);

const mapStateToProps = (state) => {
  const { searchItems } = mapSearchItemsToProps(state);

  return {
    alerts: getAlerts(state),
    sites: state.sites,
    gateways: getGateways(state),
    searchItems,
    isFleetPageEnabled: isFeatureEnabled("Fleet")(state),
    isSplitDashboardEnabled: selectSplitDashboardEnabled(state),
    isAuditLogPageEnabled: selectAuditLogPageEnabled(state),
    isMaintenancePageEnabled: isFeatureEnabled("PrivilegedUsers")(state),
  };
};

const ConnectedSidebar = withTranslation("common")(
  connect(mapStateToProps)(Sidebar)
);

function SidebarWrapper(props) {
  const canSeeApi = useCanSeeApiPage();
  return <ConnectedSidebar {...props} canSeeApi={canSeeApi} />;
}

export default SidebarWrapper;

Sidebar.defaultProps = {
  onlyAdmin: false,
};
